// v9 moduler 方式
import { firebaseApp } from '@/firebase.js'
import { getFirestore, doc, getDoc, getDocs, setDoc, addDoc, updateDoc, deleteDoc, writeBatch, arrayUnion, query, collection, serverTimestamp } from 'firebase/firestore'
import { getMyUserId, getMyUserData, getUserData } from './FirebaseUserWrapper'
const db = getFirestore(firebaseApp)

/*
 * Team関係
 */
/**
 * getTeamByTeamId
 *   teamId より team の詳細情報をDBから取得する
 *
 * @param {string} teamId
 * @returns {object}
 */
export async function getTeamByTeamId (teamId) {
  const teamRef = doc(db, 'teams', teamId)
  const teamDoc = await getDoc(teamRef)
  const team = teamDoc.data()
  return {...{id: teamId}, ...team}
}

/**
 * getTeamsByTeamIds
 *   teamId の配列を teamの詳細データに変換する
 *   クエリは並列で投げる
 *
 * @param {array<string>} teams
 * @returns {array<object>}
 */
export async function getTeamsByTeamIds (teamIds) {
  const teams = await Promise.all(teamIds.map(async (teamId) => {
    return getTeamByTeamId(teamId)
  }))
  return teams
}

/**
 * getOwnerTeamId
 *   teamId より 自身がOwnerのものをDBから取得する
 *
 * @param {string} teamId
 * @returns {object}
 */
async function getOwnerTeamId (teamId) {
  const userId = await getMyUserId()
  const memberRef = doc(db, 'teams/' + teamId + '/members', userId)
  const memberDoc = await getDoc(memberRef)
  const member = memberDoc.data()
  if (member.role === 'owner') {
    return teamId
  }
}

/**
 * getOwnerTeamIds
 *   自身がOwnerのteamIdの配列をを返却
 *   クエリは並列で投げる
 *
 * @param {array<string>} teams
 * @returns {array<object>}
 */
export async function getOwnerTeamIds (teamIds) {
  const ownerTeamIds = await Promise.all(teamIds.map(async (teamId) => {
    return getOwnerTeamId(teamId)
  }))
  return ownerTeamIds
}

/**
 * addTeam:
 *   /teams/{teamId}にteamを作成
 *   初期状態では自分のみユーザに加えた状態
 *
 *   トランザクション処理:
 *     /teams/{teamId}/members/{userId} に自分を登録する
 *     /uids/{userId}.teams を更新
 *     /socialV2/{userId}.teams を更新
 *
 *   teamの作成後の登録処理で失敗したとしても
 *   どこにもリレーションのない0人のチームができるだけなので無害
 *
 *   @returns {object} teamRef
 */
export async function addTeam (teamName) {
  const nowTime = serverTimestamp()
  const teamData = {
    createdAt: nowTime,
    members: 1,
    teamName: teamName,
    updatedAt: nowTime,
    isDeleted: false
  }
  const teamRef = await addDoc(collection(db, 'teams'), teamData)
  const teamId = teamRef.id

  const userData = await getMyUserData()
  const userId = userData.id
  const userName = userData.name

  /* トランザクション処理の開始 */
  const batch = writeBatch(db)

  /* 最初のメンバーとしてチーム作成者を追加 */
  const teamMemberRef = doc(db, 'teams/' + teamId + '/members', userId)
  const teamMemberItem = {
    createdAt: nowTime,
    name: userName,
    role: 'owner',
    updatedAt: nowTime
  }
  batch.set(teamMemberRef, teamMemberItem)

  /* チーム作成者のユーザー情報を書き換え */
  const userRef = doc(db, 'uids', userId)
  batch.update(userRef, {updatedAt: nowTime, teams: arrayUnion(teamId)})

  const socialRef = doc(db, 'socialV2', userId)
  batch.update(socialRef, {updatedAt: nowTime, teams: arrayUnion(teamId)})

  /* トランザクション処理の終了 */
  await batch.commit()
  return teamRef
}

/**
 * removeTeam:
 *   /teams/{teamId}のteamを削除
 *   削除flagをisDeletedをTrue更新して、cloud Functionsで処理を実行
 */
export async function removeTeam (teamId) {
  const nowTime = serverTimestamp()
  const teamRef = doc(db, 'teams', teamId)
  const teamData = {
    updatedAt: nowTime,
    isDeleted: true
  }
  try {
    await updateDoc(teamRef, teamData)
  } catch (err) {
    console.error(err)
  }
  return teamRef
}

/**
 * changeTeamName:
 *   /teams/{teamId}のteamNameを変更
 */
export async function changeTeamName (teamId, teamName) {
  const nowTime = serverTimestamp()
  const teamRef = doc(db, 'teams', teamId)
  const teamData = {
    updatedAt: nowTime,
    teamName: teamName
  }
  try {
    await updateDoc(teamRef, teamData)
  } catch (err) {
    console.error(err)
  }
  return teamRef
}

/**
 * addMemberToTeam:
 *   トランザクション処理:
 *     /teams/{teamId}/members/{uid}にメンバーを追加
 *     /teams/{teamId}.members + 1
 *
 *   メンバーの追加をトリガにして、CloudFunctions経由で以下が書き換わる(Admin権限が必要なため)
 *     /uids/{userId}.teams
 *     /socialV2/{userId}.teams
 *
 *   @return {teamRef} teamRefを返す
 */
export async function addMemberToTeam (teamId, userId, teamUserData) {
  const teamData = await getTeamByTeamId(teamId)

  /* トランザクション処理の開始 */
  const batch = writeBatch(db)
  const teamRef = doc(db, 'teams', teamId)

  /* メンバーの追加 */
  const nowTime = serverTimestamp()
  const userData = {
    createdAt: nowTime,
    name: teamUserData.name,
    role: teamUserData.role,
    updatedAt: nowTime
  }
  const userRef = doc(db, 'teams/' + teamId + '/members', userId)
  batch.set(userRef, userData)

  /* 人数をカウントアップ. 冪等性のためインクリメントを使わない */
  const teamMembers = teamData.members + 1
  batch.update(teamRef, {members: teamMembers, updatedAt: nowTime})

  /* トランザクション処理の終了 */
  await batch.commit()

  return teamRef
}

/**
 * addInvitedUserToTeam:
 *   トランザクション処理:
 *     /teams/{teamId}/members/{userId} に自分を登録する
 *     /uids/{userId}.teams を更新
 *     /socialV2/{userId}.teams を更新
 *
 *   teamの作成後の登録処理で失敗したとしても
 *   どこにもリレーションのない0人のチームができるだけなので無害
 *
 *   @returns {object} teamRef
 */
export async function addInvitedUserToTeam (teamId) {
  const nowTime = serverTimestamp()
  const myUserData = await getMyUserData()
  const userId = myUserData.id
  const teamData = await getTeamByTeamId(teamId)

  /* トランザクション処理の開始 */
  const batch = writeBatch(db)
  const teamRef = doc(db, 'teams', teamId)

  /* メンバーの追加 */
  const userData = {
    createdAt: nowTime,
    name: myUserData.name,
    role: 'member',
    updatedAt: nowTime
  }
  const teamMemberRef = doc(db, 'teams/' + teamId + '/members', userId)
  batch.set(teamMemberRef, userData)

  /* 人数をカウントアップ. 冪等性のためインクリメントを使わない */
  const teamMembers = teamData.members + 1
  batch.update(teamRef, {members: teamMembers, updatedAt: nowTime})

  /* チーム作成者のユーザー情報を書き換え */
  const userRef = doc(db, 'uids', userId)
  batch.update(userRef, {updatedAt: nowTime, teams: arrayUnion(teamId)})

  const socialRef = doc(db, 'socialV2', userId)
  batch.update(socialRef, {updatedAt: nowTime, teams: arrayUnion(teamId)})

  /* トランザクション処理の終了 */
  await batch.commit()
  return teamRef
}

/**
 * changeRoleOfMember:
 *   チーム内のメンバのロールを変更する
 *
 * @param {string} teamId
 * @param {string} userId
 * @param {string} role
 */
export async function changeRoleOfMember (teamId, userId, role) {
  const nowTime = serverTimestamp()
  const teamRef = doc(db, 'teams', teamId)
  const userDocRef = doc(db, 'teams/' + teamId + '/members', userId)
  try {
    await updateDoc(userDocRef, {role: role, updatedAt: nowTime})
  } catch (err) {
    throw err
  }
  return teamRef
}

/**
  * removeMemberFromTeam:
  *   メンバーをチームから削除する
  *
  *   トランザクション処理:
  *     /teams/{teamId}/members/{uid}からuserIdを削除
  *     /teams/{temId}.members - 1
  *
  *   userId削除をトリガにしてCloudFunctionsで下記ドキュメントも更新する
  *     /uids/{userId}.teams
  *     /socialV2/{userId}.teams
  *
  *   @return {teamRef} teamRefを返す
  */
export async function removeMemberFromTeam (teamId, userId) {
  const teamData = await getTeamByTeamId(teamId)

  /* トランザクション処理を開始 */
  const batch = writeBatch(db)
  const teamRef = doc(db, 'teams', teamId)
  const nowTime = serverTimestamp()

  /* ユーザをチームから削除する */
  const userRef = doc(db, 'teams/' + teamId + '/members', userId)
  batch.delete(userRef)

  /* チームのメンバ数をマイナス */
  const members = teamData.members - 1
  batch.update(teamRef, {members: members, updatedAt: nowTime})

  /* トランザクション処理の完了 */
  await batch.commit()

  return teamRef
}

/**
  * getTeamIdsByUserId:
  *   /uids/{teams}を読み取る
  *   @return {teams} teamsを返す
  */
export async function getTeamIdsByUserId (userId) {
  const userDocRef = doc(db, 'uids', userId)
  const userDoc = await getDoc(userDocRef)
  const userTeams = userDoc.data().teams
  let teams = []
  for (const teamId of userTeams) {
    teams.push(teamId)
  }
  return teams
}
/**
 * getUserIdsByTeamId:
 *   teamId より menber の詳細情報をDBから取得して返す
 *
 * @param {string} teamId
 * @returns {object}
 */
export async function getMembersByTeamId (teamId) {
  const q = query(collection(db, 'teams/' + teamId + '/members'))
  const querySnapshot = await getDocs(q)
  const members = await Promise.all(querySnapshot.docs.map(async doc => {
    const userData = await getUserData(doc.id)
    return {...{teamId: teamId}, ...{userId: doc.id}, ...{imageUrl: userData.imageUrl}, ...doc.data()}
  }))
  return members
}

/**
 * getUserIdsByTeamIds
 *   UserIds の配列を userの詳細データに変換する
 *   クエリは並列で投げる
 *
 * @param {array<string>} teamIds
 * @returns {array<object>}
 */
export async function getMembersByTeamIds (teamIds) {
  const members = await Promise.all(teamIds.map(async (teamId) => {
    return [teamId, getMembersByTeamId(teamId)]
  }))
  return members
}

/**
  * includeUserIdInTeam:
  *   /uids/{teams}/members/{userId}を読み取る
  *   @return boolean true or falseを返す
  */
export async function includeUserIdInTeam (teamId, userId) {
  const teamRef = doc(db, 'teams/' + teamId + '/members', userId)
  const memberDoc = await getDoc(teamRef)
  if (memberDoc.exists()) {
    return true
  } else {
    return false
  }
}

/**
 * addExternalMenmberToTeam
 *   /invitations/{invitedId}に招待情報を追加
 *   /teams/{teamId}/invitedMembers/{email}に情報を追加
 *   cloud Functions admin権限でSendGridから招待メールを送信
 * @returns {invitedRef}
 */
export async function addExternalMenmberToTeam (teamId, teamName, email, invitedUserId, ownerUserId, isResending) {
  const nowTime = serverTimestamp()
  // メールがある場合
  if (email) {
    // 初回送信の場合は、email情報を登録
    if (!isResending) {
      const invitedMembersRef = doc(db, 'teams/' + teamId + '/invitedMembers', email)
      const invitedMembersData = {
        createdAt: nowTime,
        updatedAt: nowTime
      }
      try {
        await setDoc(invitedMembersRef, invitedMembersData)
      } catch (err) {
        throw err
      }
    }
  // メールがない場合(userIdの場合)
  } else {
    // 初回送信の場合は、uid情報を登録
    if (!isResending) {
      const invitedMembersRef = doc(db, 'teams/' + teamId + '/invitedMembers', invitedUserId)
      const invitedMembersData = {
        createdAt: nowTime,
        updatedAt: nowTime
      }
      try {
        await setDoc(invitedMembersRef, invitedMembersData)
      } catch (err) {
        throw err
      }
    }
  }

  // invitationを作成
  const ownerData = await getMyUserData(ownerUserId)
  const invitedRef = collection(db, 'invitations')
  const invitedData = {
    createdAt: nowTime,
    email: email,
    invitedUserId: invitedUserId,
    invitingUserId: ownerUserId,
    invitingUserName: ownerData.name,
    teamId: teamId,
    teamName: teamName,
    updatedAt: nowTime
  }
  try {
    await addDoc(invitedRef, invitedData)
    return invitedRef
  } catch (err) {
    throw err
  }
}

/**
 * getInvitationFromTeam
 *   /invitations/{invitedId}にemailがあるかをチェック
 * @returns {invitedData}|{boolean}
 */
export async function getInvitationFromTeam (invitedId) {
  const myUserData = await getMyUserData()
  const invitedRef = doc(db, 'invitations', invitedId)
  const invitedDoc = await getDoc(invitedRef)
  if (!invitedDoc.exists()) { return false }
  const invitedData = invitedDoc.data()
  if (!invitedData.email && !invitedData.invitedUserId) { return false }
  if (myUserData.email === invitedData.email || myUserData.id === invitedData.invitedUserId) {
    return invitedData
  } else {
    return false
  }
}

/**
 * getInvitationForAnonymous
 *   匿名ユーザに/invitations/{invitedId}にemailがあるかをチェック
 * @returns {invitedData}|{boolean}
 */
export async function getInvitationForAnonymous (invitedId) {
  const invitedRef = doc(db, 'invitations', invitedId)
  const invitedDoc = await getDoc(invitedRef)
  if (!invitedDoc.exists()) { return false }
  const invitedData = invitedDoc.data()
  if (!invitedData.email) { return false }
  return invitedData
}

/**
 * deleteInvitedMember
 *   /teams/{teamId}/invitedMembers/{email}を削除
 * @returns {invitedRef}
 */
export async function deleteInvitedMember (teamId) {
  const userData = await getMyUserData()
  const invitedEmailRef = doc(db, 'teams/' + teamId + '/invitedMembers', userData.email)
  const invitedUidRef = doc(db, 'teams/' + teamId + '/invitedMembers', userData.id)

  const invitedEmailDoc = await getDoc(invitedEmailRef)
  const invitedUidDoc = await getDoc(invitedUidRef)
  // emailとuidの両方で招待されたケース
  if (invitedEmailDoc.exists() && invitedUidDoc.exists()) {
    try {
      await deleteDoc(invitedEmailRef)
      await deleteDoc(invitedUidRef)
      return true
    } catch (err) {
      throw err
    }
    // emailで招待されたケース
  } else if (invitedEmailDoc.exists()) {
    try {
      await deleteDoc(invitedEmailRef)
      return true
    } catch (err) {
      throw err
    }
    // uidで招待されたケース
  } else if (invitedUidDoc.exists()) {
    try {
      await deleteDoc(invitedUidRef)
      return true
    } catch (err) {
      throw err
    }
  }
}

/**
 * hasInvitedMemberInTeam
 *   /teams/{teamId}/invitedMembers/{email}が存在すればtrue
 * @returns {boolean}
 */
export async function hasInvitedMemberInTeam (teamId, email) {
  const invitedRef = doc(db, 'teams/' + teamId + '/invitedMembers', email)
  try {
    const invitedDoc = await getDoc(invitedRef)
    if (invitedDoc.exists()) {
      return true
    } else {
      return false
    }
  } catch (err) {
    return false
  }
}



// WEBPACK FOOTER //
// ./src/api/FirebaseTeamWrapper.js